import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './AdminMultiAssetBotSettings.css';
import AdminMapInboxSection from './AdminMapInbox';
import AdminMacroChartBundle from './AdminMacroChartBundle';
import AdminEveningReview from './AdminEveningReview';
import AdminMapChat from './AdminMapChat';
import './AdminMapInbox.css';
import './AdminMacroChartBundle.css';
import './AdminEveningReview.css';
import './AdminMapChat.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

export default function AdminMultiAssetBotSettings() {
  const [config, setConfig] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [mapSubTab, setMapSubTab] = useState('inbox');

  const [tickIntervalSec, setTickIntervalSec] = useState('60');
  const [hlDex, setHlDex] = useState('xyz');

  const [commissionEnabled, setCommissionEnabled] = useState(false);
  const [commissionWallet, setCommissionWallet] = useState('');
  const [commissionBpsBuy, setCommissionBpsBuy] = useState('0');
  const [commissionBpsSell, setCommissionBpsSell] = useState('0');
  const [commissionMinUsd, setCommissionMinUsd] = useState('0.01');
  const [commissionBlockWhenOwed, setCommissionBlockWhenOwed] = useState(false);
  const [commissionOwedBlockUsd, setCommissionOwedBlockUsd] = useState('1');
  const [obotMaxLeverage, setObotMaxLeverage] = useState('2');
  const [uiHideObotSignalQueue, setUiHideObotSignalQueue] = useState(false);
  const [uiHideObotOrderTransactions, setUiHideObotOrderTransactions] = useState(false);
  const [uiHideManualSignals, setUiHideManualSignals] = useState(false);
  const [builderFeeEnabled, setBuilderFeeEnabled] = useState(false);
  const [signalGateEnabled, setSignalGateEnabled] = useState(false);
  const [signalSubscriptionWeeklyUsd, setSignalSubscriptionWeeklyUsd] = useState('0');
  const [signalSubscriptionMonthlyUsd, setSignalSubscriptionMonthlyUsd] = useState('0');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cfg, st] = await Promise.all([
        axios.get(`${API_BASE}/multi-asset/config`),
        axios.get(`${API_BASE}/multi-asset/admin/settings`, { headers: authHeaders() }),
      ]);
      setConfig(cfg.data);
      setSettings(st.data);
      setTickIntervalSec(String(Math.round((st.data.tickIntervalMs || 60000) / 1000)));
      setHlDex(st.data.hlDex ?? 'xyz');
      const c = st.data.commission || {};
      setCommissionEnabled(!!c.enabled);
      setCommissionWallet(c.walletAddress || '');
      setCommissionBpsBuy(String(c.bpsBuy ?? 0));
      setCommissionBpsSell(String(c.bpsSell ?? 0));
      setCommissionMinUsd(String(c.minUsd ?? 0.01));
      setCommissionBlockWhenOwed(!!c.blockWhenOwed);
      setCommissionOwedBlockUsd(String(c.owedBlockUsd ?? 1));
      setObotMaxLeverage(String(st.data.obotMaxLeverage ?? cfg.data.obotMaxLeverage ?? 2));
      setUiHideObotSignalQueue(!!st.data.uiHideObotSignalQueue);
      setUiHideObotOrderTransactions(!!st.data.uiHideObotOrderTransactions);
      setUiHideManualSignals(!!st.data.uiHideManualSignals);
      setBuilderFeeEnabled(!!st.data.builderFeeEnabled);
      setSignalGateEnabled(!!st.data.signalGateEnabled);
      setSignalSubscriptionWeeklyUsd(String(st.data.signalSubscriptionWeeklyUsd ?? 0));
      setSignalSubscriptionMonthlyUsd(String(st.data.signalSubscriptionMonthlyUsd ?? 0));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchGlobal = async (patch) => {
    setBusy(true);
    setError(null);
    setSaveMsg(null);
    try {
      await axios.patch(`${API_BASE}/multi-asset/admin/global`, patch, {
        headers: authHeaders(),
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const savePlatformSettings = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setError(null);
    setSaveMsg(null);
    try {
      const sec = parseInt(tickIntervalSec, 10);
      if (!Number.isFinite(sec) || sec < 15 || sec > 600) {
        throw new Error('Tick interval must be between 15 and 600 seconds');
      }
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/admin/settings`,
        {
          tickIntervalMs: sec * 1000,
          hlDex: hlDex.trim(),
          commissionEnabled,
          commissionWalletAddress: commissionWallet.trim(),
          commissionBpsBuy: parseFloat(commissionBpsBuy) || 0,
          commissionBpsSell: parseFloat(commissionBpsSell) || 0,
          commissionMinUsd: parseFloat(commissionMinUsd) || 0.01,
          commissionBlockWhenOwed,
          commissionOwedBlockUsd: parseFloat(commissionOwedBlockUsd) || 1,
          obotMaxLeverage: parseFloat(obotMaxLeverage) || 2,
          uiHideObotSignalQueue,
          uiHideObotOrderTransactions,
          uiHideManualSignals,
          builderFeeEnabled,
          signalGateEnabled,
          signalSubscriptionWeeklyUsd: parseFloat(signalSubscriptionWeeklyUsd) || 0,
          signalSubscriptionMonthlyUsd: parseFloat(signalSubscriptionMonthlyUsd) || 0,
        },
        { headers: authHeaders() }
      );
      setSaveMsg(data.message || 'Settings saved.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleGlobalBot = async (enable) => {
    await patchGlobal({ enabled: enable, paperMode: config?.paperMode !== false });
  };

  const togglePaperMode = async (nextPaper) => {
    if (!nextPaper) {
      const ok = window.confirm(
        'Switch global mode to LIVE? Users must link their own wallet on Manual/oBot pages. Continue?'
      );
      if (!ok) return;
    }
    await patchGlobal({ paperMode: nextPaper });
  };

  if (loading) return <div className="loading">Loading multi-asset oBot settings…</div>;

  const globalOn = !!config?.enabled;
  const paper = config?.paperMode !== false;

  const TABS = [
    { id: 'overview', label: 'Overview & Controls' },
    { id: 'map', label: 'MAP & AI' },
    { id: 'fees', label: 'Fees & Access' },
    { id: 'trading', label: 'Trading Config' },
  ];
  const MAP_SUBTABS = [
    { id: 'inbox', label: 'MAP JSON inbox' },
    { id: 'evening', label: 'Evening Review' },
    { id: 'chat', label: 'MAP Chat' },
    { id: 'macro', label: 'Macro charts' },
  ];
  const showSaveBar = ['overview', 'fees', 'trading'].includes(activeTab);

  return (
    <div className="admin-multi-asset-bot">
      <h2>📈 Multi-asset oBot (platform admin)</h2>
      <p className="ma-admin-note">
        Hyperliquid commission is configured <strong>on this tab only</strong> — not under the separate{' '}
        <strong>💰 Commissions</strong> tab (that is for Meme Coins).
      </p>

      <nav className="ma-admin-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`ma-admin-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {error && <div className="error-message ma-admin-error">{error}</div>}
      {saveMsg && <div className="ma-admin-success">{saveMsg}</div>}

      {/* ── Overview & Controls ── */}
      <div className="ma-admin-tabpanel" role="tabpanel" hidden={activeTab !== 'overview'}>
        <p className="ma-admin-intro">
          <strong>Disable global bot</strong> stops strategy ticks only. User wallets:{' '}
          <Link to="/multi-asset/manual">Manual</Link> / <Link to="/multi-asset/obot">oBot</Link>.{' '}
          <Link to="/multi-asset/admin/omni">Omni MAP master account trading</Link> (admin override).
        </p>

        <section className="ma-admin-omni-link-card">
          <h3>Omni MAP master account</h3>
          <p>
            Admin-only trading for the platform Omni MAP book (paper or live). Open positions, liquidate all,
            or manual partial trades when AI signals are delayed — separate from user oBot pages.
          </p>
          <Link to="/multi-asset/admin/omni" className="ma-admin-omni-link-btn">
            Open Omni MAP trading →
          </Link>
        </section>

        <div className="ma-admin-stats">
          <div>
            <span className="label">Global bot</span>
            <strong className={globalOn ? 'on' : 'off'}>{globalOn ? 'ON' : 'OFF'}</strong>
          </div>
          <div>
            <span className="label">Mode</span>
            <strong className={paper ? 'paper' : 'live'}>{paper ? 'PAPER' : 'LIVE'}</strong>
          </div>
          <div>
            <span className="label">Tick</span>
            <strong>{settings?.tickIntervalMs ? settings.tickIntervalMs / 1000 : 60}s</strong>
          </div>
          <div>
            <span className="label">oBot max lev</span>
            <strong>{settings?.obotMaxLeverage ?? obotMaxLeverage}x</strong>
          </div>
          <div>
            <span className="label">Strategy lev</span>
            <strong>{config?.strategyLeverage ?? 2}x</strong>
          </div>
          <div>
            <span className="label">Commission</span>
            <strong className={commissionEnabled ? 'on' : 'off'}>{commissionEnabled ? 'ON' : 'OFF'}</strong>
          </div>
          <div>
            <span className="label">Pause if owed</span>
            <strong className={commissionBlockWhenOwed ? 'on' : 'off'}>
              {commissionBlockWhenOwed ? `ON (≥ $${commissionOwedBlockUsd})` : 'OFF'}
            </strong>
          </div>
        </div>

        <div className="ma-control-row">
          <div className="ma-control-group">
            <span className="ma-control-label">Global oBot</span>
            <div className="ma-btn-pair">
              <button
                type="button"
                className={`ma-toggle-btn ${globalOn ? 'active' : ''}`}
                disabled={busy}
                onClick={() => toggleGlobalBot(true)}
              >
                Enable
              </button>
              <button
                type="button"
                className={`ma-toggle-btn ${!globalOn ? 'active' : ''}`}
                disabled={busy}
                onClick={() => toggleGlobalBot(false)}
              >
                Disable
              </button>
            </div>
          </div>
          <div className="ma-control-group">
            <span className="ma-control-label">Trading mode (paper / live)</span>
            <div className="ma-btn-pair">
              <button
                type="button"
                className={`ma-toggle-btn ma-mode-paper ${paper ? 'active' : ''}`}
                disabled={busy}
                onClick={() => togglePaperMode(true)}
              >
                Paper
              </button>
              <button
                type="button"
                className={`ma-toggle-btn ma-mode-live ${!paper ? 'active' : ''}`}
                disabled={busy}
                onClick={() => togglePaperMode(false)}
              >
                Live
              </button>
            </div>
          </div>
        </div>

        <section id="ma-dashboard-ui" className="ma-admin-card">
          <h3>Dashboard UI visibility</h3>
          <p className="ma-card-desc">
            Hide sections on user-facing Manual and oBot pages. Bot admins still see this Admin tab.
          </p>
          <div className="form-group">
            <label className="ma-checkbox-label">
              <input
                type="checkbox"
                checked={uiHideObotSignalQueue}
                onChange={(e) => setUiHideObotSignalQueue(e.target.checked)}
              />
              Hide <strong>Signal queue</strong> on oBot Trading
            </label>
          </div>
          <div className="form-group">
            <label className="ma-checkbox-label">
              <input
                type="checkbox"
                checked={uiHideObotOrderTransactions}
                onChange={(e) => setUiHideObotOrderTransactions(e.target.checked)}
              />
              Hide <strong>Order Transactions</strong> on oBot Trading
            </label>
          </div>
          <div className="form-group">
            <label className="ma-checkbox-label">
              <input
                type="checkbox"
                checked={uiHideManualSignals}
                onChange={(e) => setUiHideManualSignals(e.target.checked)}
              />
              Hide <strong>Signals</strong> section on Manual Trades
            </label>
          </div>
          <p className="field-hint">Use “Save all platform settings” below to apply.</p>
        </section>
      </div>

      {/* ── MAP & AI ── */}
      <div className="ma-admin-tabpanel" role="tabpanel" hidden={activeTab !== 'map'}>
        <nav className="ma-admin-subtabs" role="tablist">
          {MAP_SUBTABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={mapSubTab === t.id}
              className={`ma-admin-subtab ${mapSubTab === t.id ? 'active' : ''}`}
              onClick={() => setMapSubTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div hidden={mapSubTab !== 'inbox'}>
          <AdminMapInboxSection />
        </div>
        <div hidden={mapSubTab !== 'evening'}>
          <AdminEveningReview />
        </div>
        <div hidden={mapSubTab !== 'chat'}>
          <AdminMapChat />
        </div>
        <div hidden={mapSubTab !== 'macro'}>
          <AdminMacroChartBundle />
        </div>
      </div>

      {/* ── Fees & Access ── */}
      <div className="ma-admin-tabpanel" role="tabpanel" hidden={activeTab !== 'fees'}>
        <section id="ma-commission-settings" className="ma-admin-card ma-commission-card">
          <h3>💰 Platform commission (basis points)</h3>
          <p className="ma-card-desc">
            Charged on each filled multi-asset trade. Fee = notional × BPS ÷ 10,000. Live fills send USDC from the
            user&apos;s <strong>withdrawable</strong> HL balance. Trades are blocked upfront if free USDC cannot cover
            margin + HL fee + commission + owed balance. If a fee cannot be sent after fill, it is recorded as{' '}
            <strong>owed</strong> and retried each worker tick when balance allows.
          </p>
          <div className="form-group">
            <label className="ma-checkbox-label">
              <input
                type="checkbox"
                checked={commissionEnabled}
                onChange={(e) => setCommissionEnabled(e.target.checked)}
              />
              Enable commission collection
            </label>
          </div>
          <div className="form-group">
            <label>Commission wallet (0x… receives USDC)</label>
            <input
              type="text"
              value={commissionWallet}
              onChange={(e) => setCommissionWallet(e.target.value)}
              placeholder="0x…"
              autoComplete="off"
            />
          </div>
          <div className="form-row-two">
            <div className="form-group">
              <label>Buy BPS</label>
              <input
                type="number"
                min={0}
                max={500}
                step={0.1}
                value={commissionBpsBuy}
                onChange={(e) => setCommissionBpsBuy(e.target.value)}
              />
              <span className="field-hint">Open long / close short</span>
            </div>
            <div className="form-group">
              <label>Sell BPS</label>
              <input
                type="number"
                min={0}
                max={500}
                step={0.1}
                value={commissionBpsSell}
                onChange={(e) => setCommissionBpsSell(e.target.value)}
              />
              <span className="field-hint">Open short / close long</span>
            </div>
          </div>
          <div className="form-group">
            <label>Minimum commission (USD)</label>
            <input
              type="number"
              min={0.01}
              max={100}
              step={0.01}
              value={commissionMinUsd}
              onChange={(e) => setCommissionMinUsd(e.target.value)}
            />
          </div>
        </section>

        <section id="ma-builder-fee" className="ma-admin-card ma-commission-card">
          <h3>🧾 Builder fee + signal gate (on-chain commission)</h3>
          <p className="ma-card-desc">
            <strong>Builder fee:</strong> a flat <strong>10 bps</strong> (Hyperliquid&apos;s protocol max on perps)
            deducted on-chain at every fill, <strong>in addition to</strong> the Buy/Sell commission BPS defined
            above. Example: 25 bps commission → trader pays 10 (builder, on-chain) + 25 (commission rail) = 35 bps
            per fill. The commission wallet above is the builder address. <strong>Signal gate (legacy):</strong>{' '}
            optional extra flag — Manual Trades now uses weekly/monthly signal subscriptions below.
          </p>
          <div className="form-group">
            <label className="ma-checkbox-label">
              <input
                type="checkbox"
                checked={builderFeeEnabled}
                onChange={(e) => setBuilderFeeEnabled(e.target.checked)}
              />
              Enable builder-fee commission on live fills
            </label>
          </div>
          <div className="form-group">
            <label className="ma-checkbox-label">
              <input
                type="checkbox"
                checked={signalGateEnabled}
                onChange={(e) => setSignalGateEnabled(e.target.checked)}
              />
              Gate real-time MAP signals behind builder-fee approval
            </label>
          </div>
        </section>

        <section id="ma-signal-subscription" className="ma-admin-card ma-commission-card">
          <h3>📡 Manual Trades — MAP signal access (USDC)</h3>
          <p className="ma-card-desc">
            Traders on <strong>Manual Trades</strong> choose weekly or monthly access before MAP signals appear.
            Payment is a Hyperliquid <strong>usdSend</strong> to the commission wallet above. They must also approve
            the builder fee (EIP-712) before viewing signals. Weekly access runs until 6:00 AM Central on the next
            Monday; monthly runs one calendar month from payment. Admins bypass this on Manual Trades. oBot traders
            are not charged for signal access and do not see MAP signal/trade feeds.
          </p>
          <div className="form-group">
            <label>Weekly signal access (USD)</label>
            <input
              type="number"
              min={0}
              max={10000}
              step={0.01}
              value={signalSubscriptionWeeklyUsd}
              onChange={(e) => setSignalSubscriptionWeeklyUsd(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Monthly signal access (USD)</label>
            <input
              type="number"
              min={0}
              max={10000}
              step={0.01}
              value={signalSubscriptionMonthlyUsd}
              onChange={(e) => setSignalSubscriptionMonthlyUsd(e.target.value)}
            />
          </div>
        </section>

        <section id="ma-owed-policy" className="ma-admin-card ma-owed-policy-card">
          <h3>⏸️ Pause live trading when commission is owed</h3>
          <p className="ma-card-desc">
            Default is <strong>off</strong> — trading continues and unpaid fees accrue until the user has
            withdrawable USDC. When <strong>on</strong>, new <strong>live</strong> trades (manual and oBot) are
            rejected when owed balance is at or above the threshold. Even below the threshold, each trade must
            have enough free USDC for margin + HL fee + commission + any owed balance.
          </p>
          <div className="form-group">
            <label className="ma-checkbox-label">
              <input
                type="checkbox"
                checked={commissionBlockWhenOwed}
                onChange={(e) => setCommissionBlockWhenOwed(e.target.checked)}
              />
              Pause new live trades when user owes commission
            </label>
          </div>
          <div className="form-group">
            <label>Block threshold (USD owed)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={commissionOwedBlockUsd}
              onChange={(e) => setCommissionOwedBlockUsd(e.target.value)}
              disabled={!commissionBlockWhenOwed}
            />
            <span className="field-hint">
              Example: 1 = block when owed ≥ $1.00. User sees a banner on oBot / Manual until cleared.
            </span>
          </div>
        </section>
      </div>

      {/* ── Trading Config ── */}
      <div className="ma-admin-tabpanel" role="tabpanel" hidden={activeTab !== 'trading'}>
        <section className="ma-admin-card ma-leverage-card">
          <h3>⚡ oBot leverage cap</h3>
          <p className="ma-card-desc">
            Maximum leverage users can select when queueing trades on the oBot page. Automated Omni MAP strategy
            signals always execute at <strong>{config?.strategyLeverage ?? 2}x</strong> — this setting does not
            change algorithm behavior. Manual trades on the Manual page use each asset&apos;s Hyperliquid max
            leverage instead.
          </p>
          <div className="form-group">
            <label>oBot max leverage (user cap)</label>
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              value={obotMaxLeverage}
              onChange={(e) => setObotMaxLeverage(e.target.value)}
            />
            <span className="field-hint">
              Users pick 1x–{obotMaxLeverage}x for oBot-page trades. Default user preference is 2x.
            </span>
          </div>
        </section>

        <section className="ma-admin-card ma-worker-settings-card">
          <h3 id="ma-worker-settings">⚙️ Platform worker settings</h3>
          <div className="form-group">
            <label>Worker tick interval (seconds)</label>
            <input
              type="number"
              min={15}
              max={600}
              value={tickIntervalSec}
              onChange={(e) => setTickIntervalSec(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>HL_DEX (trade.xyz)</label>
            <input type="text" value={hlDex} onChange={(e) => setHlDex(e.target.value)} placeholder="xyz" />
          </div>
        </section>
      </div>

      {showSaveBar && (
        <div className="ma-admin-savebar">
          <span className="ma-admin-savebar-hint">
            Saves all platform settings (Overview UI, Fees &amp; Access, Trading Config).
          </span>
          <button type="button" className="primary-btn ma-save-btn" disabled={busy} onClick={savePlatformSettings}>
            Save all platform settings
          </button>
        </div>
      )}
    </div>
  );
}
